import angular, { IPromise, IQService } from 'angular';
import templateUrl from './payroll-analysis.component.html'
import { PayrollService } from '../../../../core/services';
import { UtilityService } from '../../../../core/services/utility.service';
import { ICDCorporationListRM, PayrollListItem, PayrollAnalysis, RSScheduleResponseModel } from '@herbjoy/apex-api-models';
import { ProgramSelect, PayrollAnalysisAggregateFields } from '../payroll-analysis-summary/payroll-analysis-summary.component';
import { StateService } from 'angular-ui-router';

interface PayrollListItemSelect extends PayrollListItem {
    Text?: string;
}

interface StoreSelect {
    StoreID: number;
}

interface PayrollAnalysisVM extends PayrollAnalysisAggregateFields{
    entries?: PayrollAnalysis[];
}

export const PayrollAnalysisComponent = {
    bindings: {
        payrollID: "<payrollID",
        programID: "<?programID",
        storeID: "<?storeID"
    },
    templateUrl,
    controller: class PayrollAnalysisComponent {
        //@Input
        payrollID: number;
        //@Input
        programID: number;
        //@Input
        retailerID: number;
        //@Input
        storeID: number;
        loaded = false;
        showFilters = true;
        payrolls: PayrollListItemSelect[] = new kendo.data.ObservableArray([]) as any;
        programs: ProgramSelect[] = new kendo.data.ObservableArray([]) as any;
        program: ProgramSelect;
        stores: StoreSelect[] = new kendo.data.ObservableArray([]) as any;
        store: StoreSelect;
        rsSchedules: RSScheduleResponseModel[] = new kendo.data.ObservableArray([]) as any;
        payrollAnalysis: PayrollAnalysisVM;
        payroll: PayrollListItemSelect;
        icdCorporations: ICDCorporationListRM[];
        getIcdCorporations: IPromise<ICDCorporationListRM[]>;
        constructor(private $q: IQService, private $state: StateService,
            private icdCorporationService, private payrollService: PayrollService,
            private rsScheduleService, private utilityService: UtilityService,
            private apexApiUrl: string) {
            'ngInject'
        }

        $onInit() {
            this.utilityService.showLoading();

            const getPayrollAnalysis = this.loadPayrollAnalysis();
            const getPayrolls = this.loadPayrolls(this.payrollID);


            this.$q.all(
                [
                    getPayrollAnalysis,
                    getPayrolls
                ])
                .then(() => {
                    return this.loadRSSchedulesForPayroll(this.payroll);
                })
                .then(() => {
                    this.loaded = true;
                    this.changePageState();
                })
                .catch(ex => {
                    console.error(ex);
                    alert("Initial load failed");
                })
                .then(() => this.utilityService.hideLoading());

            // Will not be used directly, but resolve it in other async loaders when needed.
            this.getIcdCorporations = this.icdCorporationService.retrieveIcdCorporations();
        }

        onChangePayroll() {
            this.utilityService.showLoading();
            this.changePayroll(this.payroll)
                .then(() => this.changePageState())
                .catch(angular.noop)
                .then(() => this.utilityService.hideLoading());
        }

        onChangeProgram() {
            this.programID = this.program ? this.program.ProgramID : null;
            this.utilityService.showLoading();
            this.loadPayrollAnalysis()
                .then(() => this.changePageState())
                .catch(angular.noop)
                .then(() => this.utilityService.hideLoading());
        }

        onChangeStore() {
            this.storeID = this.store ? this.store.StoreID : null;
            this.utilityService.showLoading();
            this.loadPayrollAnalysis()
                .then(() => this.changePageState())
                .catch(angular.noop)
                .then(() => this.utilityService.hideLoading());
        }

        onClickExportAnalysis() {
            const urlStrPayroll = kendo.format(this.apexApiUrl + 'payrolls/{0}/analysis.csv?&programID={1}&storeID={2}',
                this.payroll.PayrollID, this.programID, this.storeID);
            window.location.href = urlStrPayroll;
        }

        loadPayrollAnalysis() {
            return this.payrollService.retrievePayrollAnalysis(this.payrollID, this.programID,
                this.retailerID, this.storeID)
                .then((analysis: PayrollAnalysis[]) => {
                    this.payrollAnalysis = {
                        entries: analysis.sort(function (a, b) {
                            return a.FirstName < b.FirstName
                                ? -1
                                : a.FirstName > b.FirstName
                                    ? 1
                                    : a.LastName < b.LastName
                                        ? -1
                                        : a.LastName > b.LastName
                                            ? 1
                                            : a.WeekNumber < b.WeekNumber ? -1 : 1;
                        })
                    };

                    this.payrollAnalysis.entries.reduce(function (analysis, entry) {
                        // Create aggregate totals for the analysis.
                        analysis.RegularHours = (analysis.RegularHours || 0) + entry.RegularHours;
                        analysis.OvertimeHours = (analysis.OvertimeHours || 0) + entry.OvertimeHours;
                        analysis.DoubleTimeHours = (analysis.DoubleTimeHours || 0) + entry.DoubleTimeHours;
                        analysis.GeneralHours = (analysis.GeneralHours || 0) + entry.GeneralHours;
                        analysis.UnproductiveHours = (analysis.UnproductiveHours || 0) + entry.UnproductiveHours;
                        analysis.PaidHours = (analysis.PaidHours || 0) + entry.PaidHours;
                        analysis.TotalHours = (analysis.TotalHours || 0) + entry.TotalHours;
                        analysis.TotalGuaranteed = (analysis.TotalGuaranteed || 0) + entry.TotalGuaranteed;
                        analysis.RegularCommissionPaid = (analysis.RegularCommissionPaid || 0)
                            + entry.RegularCommissionPaid;
                        analysis.ExtraCommissionPaid = (analysis.ExtraCommissionPaid || 0) + entry.ExtraCommissionPaid;
                        analysis.MinimumWageTopUp = (analysis.MinimumWageTopUp || 0) + entry.MinimumWageTopUp;
                        analysis.OvertimePremium = (analysis.OvertimePremium || 0) + entry.OvertimePremium;
                        analysis.DoubleTimePremium = (analysis.DoubleTimePremium || 0) + entry.DoubleTimePremium;
                        analysis.Salary = (analysis.Salary || 0) + entry.Salary;
                        analysis.Bonus = (analysis.Bonus || 0) + entry.Bonus;
                        analysis.CommissionBonus = (analysis.CommissionBonus || 0) + (entry.CommissionBonus || 0);
                        analysis.TravelAllowance = (analysis.TravelAllowance || 0) + entry.TravelAllowance;
                        analysis.CellAllowance = (analysis.CellAllowance || 0) + entry.CellAllowance;
                        analysis.Tip = (analysis.Tip || 0) + entry.Tip;
                        analysis.SplitTimeAmount = (analysis.SplitTimeAmount || 0) + entry.SplitTimeAmount;
                        analysis.SickPayAmount = (analysis.SickPayAmount || 0) + entry.SickPayAmount;
                        analysis.HolidayPay = (analysis.HolidayPay || 0) + entry.HolidayPay;
                        analysis.GrossPay = (analysis.GrossPay || 0) + entry.GrossPay;
                        analysis.NetPayAddition = (analysis.NetPayAddition || 0) + entry.NetPayAddition;
                        analysis.NetPayDeduction = (analysis.NetPayDeduction || 0) + entry.NetPayDeduction;

                        // Show columns if any of the entries show the column.
                        analysis.ShowDT = analysis.ShowDT || entry.ShowDT;
                        analysis.ShowTravel = analysis.ShowTravel || entry.ShowTravel;
                        analysis.ShowCell = analysis.ShowCell || entry.ShowCell;
                        analysis.ShowTip = analysis.ShowTip || entry.ShowTip;
                        analysis.ShowSplit = analysis.ShowSplit || entry.ShowSplit;
                        analysis.ShowSick = analysis.ShowSick || entry.ShowSick;
                        analysis.ShowHolidayPay = analysis.ShowHolidayPay || entry.ShowHolidayPay;

                        return analysis;
                    }, this.payrollAnalysis);
                    return this.payrollAnalysis;
                });
        }

        loadPayrolls(payrollID: number) {
            // Look up the payroll with the given ID to find the corporation id.
            return this.payrollService.retrievePayrolls({})
                .then((payrolls) => {
                    const payroll = payrolls.filter(function (payroll) {
                        return payroll.PayrollID === payrollID;
                    })[0];

                    if (payroll) {
                        // Only include payrolls belonging to the specific corporation.
                        return this.payrollService.retrieveIcdCorporationPayrolls(payroll.IcdCorporationID);
                    }
                    // Else, the analysis also didn't load, so the page will be empty either way.
                    return [];
                })
                .then((payrolls: PayrollListItem[]) => {
                    // Data only exists for locked payrolls.
                    return payrolls.filter(function (payroll) {
                        return payroll.Locked;
                    });
                })
                .then((payrolls: PayrollListItemSelect[]) => {
                    payrolls.forEach(function (payroll) {
                        payroll.Text = kendo.format("{0:MM/dd/yyyy} - {1:MM/dd/yyyy}", payroll.StartDate, payroll.EndDate);
                    });

                    this.payrolls.push(...payrolls);
                    this.payroll = this.payrolls.filter(function (payroll) {
                        return payroll.PayrollID === payrollID;
                    })[0];
                });
        }

        loadRSSchedulesForPayroll(payroll) {
            this.rsSchedules.splice(0, this.rsSchedules.length);
            this.programs.splice(0, this.programs.length);
            this.stores.splice(0, this.stores.length);
            if (!payroll) {
                return this.$q.when(this.rsSchedules);
            }

            return this.rsScheduleService.retrieveRSSchedules(payroll)
                .then(rsSchedules => {
                    rsSchedules = rsSchedules.filter(rsSchedule => {
                        return rsSchedule.IcdCorporationID === payroll.IcdCorporationID;
                    });

                    this.rsSchedules.push(...rsSchedules);

                    // Get distinct lists of programs and stores from the schedule.
                    const programs = rsSchedules.reduce((setList, program) => {
                        if (!setList.set[program.ProgramID]) {
                            setList.set[program.ProgramID] = true;
                            setList.list.push(program);
                        }
                        return setList;
                    }, { set: {}, list: [] }).list.sort((a, b) => {
                        return a.Program < b.Program ? -1 : 1;
                    });
                    this.programs.push(...programs);
                    this.program = this.programs.filter(program => {
                        return program.ProgramID === this.programID;
                    })[0];

                    const stores = rsSchedules.reduce((setList, store) => {
                        if (!setList.set[store.StoreID]) {
                            setList.set[store.StoreID] = true;
                            setList.list.push(store);
                        }
                        return setList;
                    }, { set: {}, list: [] }).list.sort((a, b) => {
                        return a.StoreNumber < b.StoreNumber ? -1 : 1;
                    }).map(p => ({
                        StoreID: p.StoreID,
                        text: `${p.Retailer} #${p.StoreNumber}`
                    }));
                    this.stores.push(...stores);
                    this.store = this.stores.filter(store => {
                        return store.StoreID === this.storeID;
                    })[0];
                });
        }

        changePayroll(payroll) {
            this.payrollID = payroll.PayrollID;
            return this.loadRSSchedulesForPayroll(payroll)
                .then(() => this.loadPayrollAnalysis);
        }

        changePageState() {
            const query = {
                payrollID: this.payroll.PayrollID,
                programID: this.programID,
                storeID: this.storeID,
            }
            this.$state.go(".", query);
        }

        showOnlyFilteredColumns() {
            return !(this.program && this.program.ProgramID)
                && !(this.store && this.store.StoreID);
        }
    },
    controllerAs: "vm"
};
